/**
 * Language Options for Course and Certification Content
 *
 * Comprehensive list of supported languages with their locale codes.
 * Used across course creation, certification exams, and content generation.
 */

export interface LanguageOption {
  label: string;
  value: string;
}

export interface LanguageOptionDetailed {
  code: string;
  name: string;
  flag: string;
}

export const languageOptions: LanguageOption[] = [
  // English Variants
  { label: '🇺🇸 English (US)', value: 'en-US' },
  { label: '🇬🇧 English (UK)', value: 'en-GB' },
  { label: '🇦🇺 English (Australia)', value: 'en-AU' },
  { label: '🇮🇳 English (India)', value: 'en-IN' },

  // Indian Regional Languages
  { label: '🇮🇳 Hindi (हिन्दी)', value: 'hi-IN' },
  { label: '🇮🇳 Bengali (বাংলা)', value: 'bn-IN' },
  { label: '🇮🇳 Telugu (తెలుగు)', value: 'te-IN' },
  { label: '🇮🇳 Marathi (मराठी)', value: 'mr-IN' },
  { label: '🇮🇳 Tamil (தமிழ்)', value: 'ta-IN' },
  { label: '🇮🇳 Gujarati (ગુજરાતી)', value: 'gu-IN' },
  { label: '🇮🇳 Kannada (ಕನ್ನಡ)', value: 'kn-IN' },
  { label: '🇮🇳 Malayalam (മലയാളം)', value: 'ml-IN' },
  { label: '🇮🇳 Punjabi (ਪੰਜਾਬੀ)', value: 'pa-IN' },
  { label: '🇮🇳 Urdu (اردو)', value: 'ur-IN' },

  // International Languages
  { label: '🇪🇸 Spanish (Spain)', value: 'es-ES' },
  { label: '🇫🇷 French', value: 'fr-FR' },
  { label: '🇩🇪 German', value: 'de-DE' },
  { label: '🇵🇹 Portuguese (Brazil)', value: 'pt-BR' },
  { label: '🇮🇹 Italian', value: 'it-IT' },
  { label: '🇨🇳 Chinese (Simplified)', value: 'zh-CN' },
  { label: '🇯🇵 Japanese', value: 'ja-JP' },
  { label: '🇰🇷 Korean', value: 'ko-KR' },
  { label: '🇸🇦 Arabic', value: 'ar-SA' },
];

/**
 * Get language display name from language code
 * @param code - Language code (e.g., 'hi-IN', 'en-US')
 * @returns Formatted language name (e.g., 'Hindi (हिन्दी)')
 */
export function getLanguageLabel(code: string): string {
  const language = languageOptions.find(lang => lang.value === code);
  return language ? language.label : code;
}

/**
 * Get language name without emoji
 * @param code - Language code (e.g., 'hi-IN', 'en-US')
 * @returns Language name without emoji (e.g., 'Hindi (हिन्दी)')
 */
/** Drop a leading flag emoji. Windows has no flag glyphs and shows the two
 *  letters of the country code instead, so a label arrives as "IN Hindi". */
export function stripFlagEmoji(text: string): string {
  return String(text ?? '').replace(/^[\u{1F1E6}-\u{1F1FF}]{2}\s*/u, '');
}

export function getLanguageName(code: string): string {
  const label = getLanguageLabel(code);
  return stripFlagEmoji(label);
}

/**
 * Check if a language code is English variant
 * @param code - Language code
 * @returns True if English variant
 */
export function isEnglishLanguage(code: string): boolean {
  return ['en-US', 'en-GB', 'en-AU', 'en-IN'].includes(code);
}

/**
 * Get all English variant codes
 */
export const englishLanguageCodes = ['en-US', 'en-GB', 'en-AU', 'en-IN'];

/**
 * Get all Indian regional language codes
 */
export const indianLanguageCodes = [
  'hi-IN', 'bn-IN', 'te-IN', 'mr-IN', 'ta-IN',
  'gu-IN', 'kn-IN', 'ml-IN', 'pa-IN', 'ur-IN'
];

/**
 * Get all supported language codes
 */
export const allLanguageCodes = languageOptions.map(lang => lang.value);

/**
 * Voice types supported by Google Cloud TTS
 * Reference: https://docs.cloud.google.com/text-to-speech/docs/voices
 */
export type VoiceType =
  | 'Chirp3-HD'
  | 'Chirp-HD'
  | 'Studio'
  | 'Neural2'
  | 'News'
  | 'Casual'
  | 'Polyglot'
  | 'WaveNet'
  | 'Standard';

export interface VoiceTypeOption {
  value: VoiceType;
  label: string;
  description?: string;
}

export type SSMLGender = 'MALE' | 'FEMALE' | 'NEUTRAL';

export interface VoiceInfo {
  language_code: string;
  voice_name: string;
  ssml_gender: SSMLGender;
  voice_type: VoiceType;
}

/**
 * Chirp3-HD voices are the same 30 character names across ALL languages.
 * This generator avoids duplicating 630+ entries manually.
 * Reference: https://docs.cloud.google.com/text-to-speech/docs/list-voices-and-types
 */
const CHIRP3_HD_VOICES: { name: string; gender: SSMLGender }[] = [
  { name: 'Achernar', gender: 'FEMALE' },
  { name: 'Achird', gender: 'MALE' },
  { name: 'Algenib', gender: 'MALE' },
  { name: 'Algieba', gender: 'MALE' },
  { name: 'Alnilam', gender: 'MALE' },
  { name: 'Aoede', gender: 'FEMALE' },
  { name: 'Autonoe', gender: 'FEMALE' },
  { name: 'Callirrhoe', gender: 'FEMALE' },
  { name: 'Charon', gender: 'MALE' },
  { name: 'Despina', gender: 'FEMALE' },
  { name: 'Enceladus', gender: 'MALE' },
  { name: 'Erinome', gender: 'FEMALE' },
  { name: 'Fenrir', gender: 'MALE' },
  { name: 'Gacrux', gender: 'FEMALE' },
  { name: 'Iapetus', gender: 'MALE' },
  { name: 'Kore', gender: 'FEMALE' },
  { name: 'Laomedeia', gender: 'FEMALE' },
  { name: 'Leda', gender: 'FEMALE' },
  { name: 'Orus', gender: 'MALE' },
  { name: 'Puck', gender: 'MALE' },
  { name: 'Pulcherrima', gender: 'FEMALE' },
  { name: 'Rasalgethi', gender: 'MALE' },
  { name: 'Sadachbia', gender: 'MALE' },
  { name: 'Sadaltager', gender: 'MALE' },
  { name: 'Schedar', gender: 'MALE' },
  { name: 'Sulafat', gender: 'FEMALE' },
  { name: 'Umbriel', gender: 'MALE' },
  { name: 'Vindemiatrix', gender: 'FEMALE' },
  { name: 'Zephyr', gender: 'FEMALE' },
  { name: 'Zubenelgenubi', gender: 'MALE' },
];

/**
 * Generate all Chirp3-HD VoiceInfo entries for a given language code.
 * @param language_code - UI language code (e.g. 'en-US')
 * @param tts_prefix - TTS API prefix if different from language_code (e.g. 'cmn-CN' for zh-CN, 'ar-XA' for ar-SA)
 */
function generateChirp3HDVoices(language_code: string, tts_prefix?: string): VoiceInfo[] {
  const prefix = tts_prefix || language_code;
  return CHIRP3_HD_VOICES.map(v => ({
    language_code,
    voice_name: `${prefix}-Chirp3-HD-${v.name}`,
    ssml_gender: v.gender,
    voice_type: 'Chirp3-HD' as VoiceType,
  }));
}

/**
 * Non-Chirp3-HD voices per language (Neural2, WaveNet, Standard, Studio, News, etc.)
 * Chirp3-HD voices are generated automatically via generateChirp3HDVoices().
 * Reference: https://docs.cloud.google.com/text-to-speech/docs/voices
 */
const staticVoicesByLanguage: Record<string, VoiceInfo[]> = {
  // ── English (US) ──────────────────────────────────────────
  'en-US': [
    { language_code: 'en-US', voice_name: 'en-US-Chirp3-HD-Charon', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'en-US', voice_name: 'en-US-Chirp3-HD-Fenrir', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'en-US', voice_name: 'en-US-Chirp3-HD-Kore', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'en-US', voice_name: 'en-US-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    // Chirp HD
    { language_code: 'en-US', voice_name: 'en-US-Chirp-HD-D', ssml_gender: 'MALE', voice_type: 'Chirp-HD' },
    { language_code: 'en-US', voice_name: 'en-US-Chirp-HD-F', ssml_gender: 'FEMALE', voice_type: 'Chirp-HD' },
    { language_code: 'en-US', voice_name: 'en-US-Chirp-HD-O', ssml_gender: 'FEMALE', voice_type: 'Chirp-HD' },
    // Studio
    { language_code: 'en-US', voice_name: 'en-US-Studio-O', ssml_gender: 'FEMALE', voice_type: 'Studio' },
    { language_code: 'en-US', voice_name: 'en-US-Studio-Q', ssml_gender: 'MALE', voice_type: 'Studio' },
    // Neural2
    { language_code: 'en-US', voice_name: 'en-US-Neural2-A', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'en-US', voice_name: 'en-US-Neural2-C', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'en-US', voice_name: 'en-US-Neural2-D', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'en-US', voice_name: 'en-US-Neural2-E', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'en-US', voice_name: 'en-US-Neural2-F', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'en-US', voice_name: 'en-US-Neural2-G', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'en-US', voice_name: 'en-US-Neural2-H', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'en-US', voice_name: 'en-US-Neural2-I', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'en-US', voice_name: 'en-US-Neural2-J', ssml_gender: 'MALE', voice_type: 'Neural2' },
    // News
    { language_code: 'en-US', voice_name: 'en-US-News-K', ssml_gender: 'FEMALE', voice_type: 'News' },
    { language_code: 'en-US', voice_name: 'en-US-News-L', ssml_gender: 'FEMALE', voice_type: 'News' },
    { language_code: 'en-US', voice_name: 'en-US-News-N', ssml_gender: 'MALE', voice_type: 'News' },
    // Casual
    { language_code: 'en-US', voice_name: 'en-US-Casual-K', ssml_gender: 'MALE', voice_type: 'Casual' },
    // Polyglot
    { language_code: 'en-US', voice_name: 'en-US-Polyglot-1', ssml_gender: 'MALE', voice_type: 'Polyglot' },
    // WaveNet
    { language_code: 'en-US', voice_name: 'en-US-Wavenet-A', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'en-US', voice_name: 'en-US-Wavenet-B', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'en-US', voice_name: 'en-US-Wavenet-C', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'en-US', voice_name: 'en-US-Wavenet-D', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'en-US', voice_name: 'en-US-Wavenet-E', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'en-US', voice_name: 'en-US-Wavenet-F', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'en-US', voice_name: 'en-US-Wavenet-G', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'en-US', voice_name: 'en-US-Wavenet-H', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'en-US', voice_name: 'en-US-Wavenet-I', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'en-US', voice_name: 'en-US-Wavenet-J', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    // Standard
    { language_code: 'en-US', voice_name: 'en-US-Standard-A', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'en-US', voice_name: 'en-US-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'en-US', voice_name: 'en-US-Standard-C', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'en-US', voice_name: 'en-US-Standard-D', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'en-US', voice_name: 'en-US-Standard-E', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'en-US', voice_name: 'en-US-Standard-F', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'en-US', voice_name: 'en-US-Standard-G', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'en-US', voice_name: 'en-US-Standard-H', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'en-US', voice_name: 'en-US-Standard-I', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'en-US', voice_name: 'en-US-Standard-J', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── English (UK) ──────────────────────────────────────────
  'en-GB': [
    { language_code: 'en-GB', voice_name: 'en-GB-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'en-GB', voice_name: 'en-GB-Chirp3-HD-Charon', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'en-GB', voice_name: 'en-GB-Chirp3-HD-Kore', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'en-GB', voice_name: 'en-GB-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'en-GB', voice_name: 'en-GB-Chirp-HD-D', ssml_gender: 'MALE', voice_type: 'Chirp-HD' },
    { language_code: 'en-GB', voice_name: 'en-GB-Chirp-HD-F', ssml_gender: 'FEMALE', voice_type: 'Chirp-HD' },
    { language_code: 'en-GB', voice_name: 'en-GB-Chirp-HD-O', ssml_gender: 'FEMALE', voice_type: 'Chirp-HD' },
    { language_code: 'en-GB', voice_name: 'en-GB-Studio-B', ssml_gender: 'MALE', voice_type: 'Studio' },
    { language_code: 'en-GB', voice_name: 'en-GB-Studio-C', ssml_gender: 'FEMALE', voice_type: 'Studio' },
    { language_code: 'en-GB', voice_name: 'en-GB-Neural2-A', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'en-GB', voice_name: 'en-GB-Neural2-B', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'en-GB', voice_name: 'en-GB-Neural2-C', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'en-GB', voice_name: 'en-GB-Neural2-D', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'en-GB', voice_name: 'en-GB-Neural2-F', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'en-GB', voice_name: 'en-GB-Neural2-N', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'en-GB', voice_name: 'en-GB-Neural2-O', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'en-GB', voice_name: 'en-GB-News-G', ssml_gender: 'FEMALE', voice_type: 'News' },
    { language_code: 'en-GB', voice_name: 'en-GB-News-H', ssml_gender: 'FEMALE', voice_type: 'News' },
    { language_code: 'en-GB', voice_name: 'en-GB-News-I', ssml_gender: 'FEMALE', voice_type: 'News' },
    { language_code: 'en-GB', voice_name: 'en-GB-News-J', ssml_gender: 'MALE', voice_type: 'News' },
    { language_code: 'en-GB', voice_name: 'en-GB-News-K', ssml_gender: 'MALE', voice_type: 'News' },
    { language_code: 'en-GB', voice_name: 'en-GB-News-L', ssml_gender: 'MALE', voice_type: 'News' },
    { language_code: 'en-GB', voice_name: 'en-GB-News-M', ssml_gender: 'MALE', voice_type: 'News' },
    { language_code: 'en-GB', voice_name: 'en-GB-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'en-GB', voice_name: 'en-GB-Wavenet-B', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'en-GB', voice_name: 'en-GB-Wavenet-C', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'en-GB', voice_name: 'en-GB-Wavenet-D', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'en-GB', voice_name: 'en-GB-Wavenet-F', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'en-GB', voice_name: 'en-GB-Wavenet-N', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'en-GB', voice_name: 'en-GB-Wavenet-O', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'en-GB', voice_name: 'en-GB-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'en-GB', voice_name: 'en-GB-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'en-GB', voice_name: 'en-GB-Standard-C', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'en-GB', voice_name: 'en-GB-Standard-D', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'en-GB', voice_name: 'en-GB-Standard-F', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'en-GB', voice_name: 'en-GB-Standard-N', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'en-GB', voice_name: 'en-GB-Standard-O', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── English (Australia) ───────────────────────────────────
  'en-AU': [
    { language_code: 'en-AU', voice_name: 'en-AU-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'en-AU', voice_name: 'en-AU-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'en-AU', voice_name: 'en-AU-Chirp-HD-D', ssml_gender: 'MALE', voice_type: 'Chirp-HD' },
    { language_code: 'en-AU', voice_name: 'en-AU-Chirp-HD-F', ssml_gender: 'FEMALE', voice_type: 'Chirp-HD' },
    { language_code: 'en-AU', voice_name: 'en-AU-Chirp-HD-O', ssml_gender: 'FEMALE', voice_type: 'Chirp-HD' },
    { language_code: 'en-AU', voice_name: 'en-AU-Neural2-A', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'en-AU', voice_name: 'en-AU-Neural2-B', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'en-AU', voice_name: 'en-AU-Neural2-C', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'en-AU', voice_name: 'en-AU-Neural2-D', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'en-AU', voice_name: 'en-AU-News-E', ssml_gender: 'FEMALE', voice_type: 'News' },
    { language_code: 'en-AU', voice_name: 'en-AU-News-F', ssml_gender: 'FEMALE', voice_type: 'News' },
    { language_code: 'en-AU', voice_name: 'en-AU-News-G', ssml_gender: 'MALE', voice_type: 'News' },
    { language_code: 'en-AU', voice_name: 'en-AU-Polyglot-1', ssml_gender: 'MALE', voice_type: 'Polyglot' },
    { language_code: 'en-AU', voice_name: 'en-AU-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'en-AU', voice_name: 'en-AU-Wavenet-B', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'en-AU', voice_name: 'en-AU-Wavenet-C', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'en-AU', voice_name: 'en-AU-Wavenet-D', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'en-AU', voice_name: 'en-AU-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'en-AU', voice_name: 'en-AU-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'en-AU', voice_name: 'en-AU-Standard-C', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'en-AU', voice_name: 'en-AU-Standard-D', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── English (India) ───────────────────────────────────────
  // Restricted to the variants that exist in BOTH en-IN and en-GB.
  //
  // Why: en-IN courses are SPOKEN and SUBTITLED as en-GB (the en-IN voice and
  // recogniser dropped words from the burned-in scene subtitles), and the backend
  // does that by swapping the locale prefix at generation time
  // (LanguageCodeMapper.resolveBackendVoiceName: en-IN-Neural2-C -> en-GB-Neural2-C).
  // Listing only the shared variants guarantees that swap always lands on a real
  // en-GB voice.
  //
  // The names stay en-IN-* so they pair with the en-IN language_code the UI sends —
  // /api/tts/preview posts formData.languageCode, and an en-GB voice with en-IN is
  // rejected by Google ("language code doesn't match the voice").
  //
  // Dropped vs the full en-IN catalogue: Wavenet-E and Standard-E (no en-GB E).
  // Not added from en-GB: Studio and News (no en-IN equivalent), and Neural2/
  // WaveNet/Standard F/N/O beyond F.
  'en-IN': [
    { language_code: 'en-IN', voice_name: 'en-IN-Chirp-HD-D', ssml_gender: 'MALE', voice_type: 'Chirp-HD' },
    { language_code: 'en-IN', voice_name: 'en-IN-Chirp-HD-F', ssml_gender: 'FEMALE', voice_type: 'Chirp-HD' },
    { language_code: 'en-IN', voice_name: 'en-IN-Chirp-HD-O', ssml_gender: 'FEMALE', voice_type: 'Chirp-HD' },
    // Gender shown is the en-GB counterpart's, because en-GB is the voice that
    // actually speaks the course. It differs from the en-IN voice for C, D and F.
    { language_code: 'en-IN', voice_name: 'en-IN-Neural2-A', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'en-IN', voice_name: 'en-IN-Neural2-B', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'en-IN', voice_name: 'en-IN-Neural2-C', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'en-IN', voice_name: 'en-IN-Neural2-D', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'en-IN', voice_name: 'en-IN-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'en-IN', voice_name: 'en-IN-Wavenet-B', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'en-IN', voice_name: 'en-IN-Wavenet-C', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'en-IN', voice_name: 'en-IN-Wavenet-D', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'en-IN', voice_name: 'en-IN-Wavenet-F', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'en-IN', voice_name: 'en-IN-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'en-IN', voice_name: 'en-IN-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'en-IN', voice_name: 'en-IN-Standard-C', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'en-IN', voice_name: 'en-IN-Standard-D', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'en-IN', voice_name: 'en-IN-Standard-F', ssml_gender: 'FEMALE', voice_type: 'Standard' },
  ],

  // ── Hindi ─────────────────────────────────────────────────
  'hi-IN': [
    { language_code: 'hi-IN', voice_name: 'hi-IN-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Neural2-A', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Neural2-B', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Neural2-C', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Neural2-D', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Wavenet-B', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Wavenet-C', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Wavenet-D', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Standard-C', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Standard-D', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Wavenet-E', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Wavenet-F', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Standard-E', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'hi-IN', voice_name: 'hi-IN-Standard-F', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── Tamil ─────────────────────────────────────────────────
  'ta-IN': [
    { language_code: 'ta-IN', voice_name: 'ta-IN-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'ta-IN', voice_name: 'ta-IN-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'ta-IN', voice_name: 'ta-IN-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'ta-IN', voice_name: 'ta-IN-Wavenet-B', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'ta-IN', voice_name: 'ta-IN-Wavenet-C', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'ta-IN', voice_name: 'ta-IN-Wavenet-D', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'ta-IN', voice_name: 'ta-IN-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'ta-IN', voice_name: 'ta-IN-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'ta-IN', voice_name: 'ta-IN-Standard-C', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'ta-IN', voice_name: 'ta-IN-Standard-D', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── Bengali ───────────────────────────────────────────────
  'bn-IN': [
    { language_code: 'bn-IN', voice_name: 'bn-IN-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'bn-IN', voice_name: 'bn-IN-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'bn-IN', voice_name: 'bn-IN-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'bn-IN', voice_name: 'bn-IN-Wavenet-B', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'bn-IN', voice_name: 'bn-IN-Wavenet-C', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'bn-IN', voice_name: 'bn-IN-Wavenet-D', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'bn-IN', voice_name: 'bn-IN-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'bn-IN', voice_name: 'bn-IN-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'bn-IN', voice_name: 'bn-IN-Standard-C', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'bn-IN', voice_name: 'bn-IN-Standard-D', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── Telugu ────────────────────────────────────────────────
  'te-IN': [
    { language_code: 'te-IN', voice_name: 'te-IN-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'te-IN', voice_name: 'te-IN-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'te-IN', voice_name: 'te-IN-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'te-IN', voice_name: 'te-IN-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'te-IN', voice_name: 'te-IN-Standard-C', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'te-IN', voice_name: 'te-IN-Standard-D', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── Marathi ───────────────────────────────────────────────
  'mr-IN': [
    { language_code: 'mr-IN', voice_name: 'mr-IN-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'mr-IN', voice_name: 'mr-IN-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'mr-IN', voice_name: 'mr-IN-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'mr-IN', voice_name: 'mr-IN-Wavenet-B', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'mr-IN', voice_name: 'mr-IN-Wavenet-C', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'mr-IN', voice_name: 'mr-IN-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'mr-IN', voice_name: 'mr-IN-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'mr-IN', voice_name: 'mr-IN-Standard-C', ssml_gender: 'FEMALE', voice_type: 'Standard' },
  ],

  // ── Gujarati ──────────────────────────────────────────────
  'gu-IN': [
    { language_code: 'gu-IN', voice_name: 'gu-IN-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'gu-IN', voice_name: 'gu-IN-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'gu-IN', voice_name: 'gu-IN-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'gu-IN', voice_name: 'gu-IN-Wavenet-B', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'gu-IN', voice_name: 'gu-IN-Wavenet-C', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'gu-IN', voice_name: 'gu-IN-Wavenet-D', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'gu-IN', voice_name: 'gu-IN-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'gu-IN', voice_name: 'gu-IN-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'gu-IN', voice_name: 'gu-IN-Standard-C', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'gu-IN', voice_name: 'gu-IN-Standard-D', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── Kannada ───────────────────────────────────────────────
  'kn-IN': [
    { language_code: 'kn-IN', voice_name: 'kn-IN-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'kn-IN', voice_name: 'kn-IN-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'kn-IN', voice_name: 'kn-IN-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'kn-IN', voice_name: 'kn-IN-Wavenet-B', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'kn-IN', voice_name: 'kn-IN-Wavenet-C', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'kn-IN', voice_name: 'kn-IN-Wavenet-D', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'kn-IN', voice_name: 'kn-IN-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'kn-IN', voice_name: 'kn-IN-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'kn-IN', voice_name: 'kn-IN-Standard-C', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'kn-IN', voice_name: 'kn-IN-Standard-D', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── Malayalam ─────────────────────────────────────────────
  'ml-IN': [
    { language_code: 'ml-IN', voice_name: 'ml-IN-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'ml-IN', voice_name: 'ml-IN-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'ml-IN', voice_name: 'ml-IN-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'ml-IN', voice_name: 'ml-IN-Wavenet-B', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'ml-IN', voice_name: 'ml-IN-Wavenet-C', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'ml-IN', voice_name: 'ml-IN-Wavenet-D', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'ml-IN', voice_name: 'ml-IN-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'ml-IN', voice_name: 'ml-IN-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'ml-IN', voice_name: 'ml-IN-Standard-C', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'ml-IN', voice_name: 'ml-IN-Standard-D', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── Punjabi ───────────────────────────────────────────────
  'pa-IN': [
    { language_code: 'pa-IN', voice_name: 'pa-IN-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'pa-IN', voice_name: 'pa-IN-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'pa-IN', voice_name: 'pa-IN-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'pa-IN', voice_name: 'pa-IN-Wavenet-B', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'pa-IN', voice_name: 'pa-IN-Wavenet-C', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'pa-IN', voice_name: 'pa-IN-Wavenet-D', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'pa-IN', voice_name: 'pa-IN-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'pa-IN', voice_name: 'pa-IN-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'pa-IN', voice_name: 'pa-IN-Standard-C', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'pa-IN', voice_name: 'pa-IN-Standard-D', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── Urdu ──────────────────────────────────────────────────
  'ur-IN': [
    { language_code: 'ur-IN', voice_name: 'ur-IN-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'ur-IN', voice_name: 'ur-IN-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'ur-IN', voice_name: 'ur-IN-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'ur-IN', voice_name: 'ur-IN-Wavenet-B', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'ur-IN', voice_name: 'ur-IN-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'ur-IN', voice_name: 'ur-IN-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── Spanish ───────────────────────────────────────────────
  'es-ES': [
    { language_code: 'es-ES', voice_name: 'es-ES-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'es-ES', voice_name: 'es-ES-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'es-ES', voice_name: 'es-ES-Chirp-HD-D', ssml_gender: 'MALE', voice_type: 'Chirp-HD' },
    { language_code: 'es-ES', voice_name: 'es-ES-Chirp-HD-F', ssml_gender: 'FEMALE', voice_type: 'Chirp-HD' },
    { language_code: 'es-ES', voice_name: 'es-ES-Chirp-HD-O', ssml_gender: 'FEMALE', voice_type: 'Chirp-HD' },
    { language_code: 'es-ES', voice_name: 'es-ES-Studio-C', ssml_gender: 'FEMALE', voice_type: 'Studio' },
    { language_code: 'es-ES', voice_name: 'es-ES-Studio-F', ssml_gender: 'MALE', voice_type: 'Studio' },
    { language_code: 'es-ES', voice_name: 'es-ES-Neural2-A', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'es-ES', voice_name: 'es-ES-Neural2-E', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'es-ES', voice_name: 'es-ES-Neural2-F', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'es-ES', voice_name: 'es-ES-Neural2-G', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'es-ES', voice_name: 'es-ES-Neural2-H', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'es-ES', voice_name: 'es-ES-Polyglot-1', ssml_gender: 'MALE', voice_type: 'Polyglot' },
    { language_code: 'es-ES', voice_name: 'es-ES-Wavenet-E', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'es-ES', voice_name: 'es-ES-Wavenet-F', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'es-ES', voice_name: 'es-ES-Wavenet-G', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'es-ES', voice_name: 'es-ES-Wavenet-H', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'es-ES', voice_name: 'es-ES-Standard-E', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'es-ES', voice_name: 'es-ES-Standard-F', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'es-ES', voice_name: 'es-ES-Standard-G', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'es-ES', voice_name: 'es-ES-Standard-H', ssml_gender: 'FEMALE', voice_type: 'Standard' },
  ],

  // ── French ────────────────────────────────────────────────
  'fr-FR': [
    { language_code: 'fr-FR', voice_name: 'fr-FR-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'fr-FR', voice_name: 'fr-FR-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'fr-FR', voice_name: 'fr-FR-Chirp-HD-D', ssml_gender: 'MALE', voice_type: 'Chirp-HD' },
    { language_code: 'fr-FR', voice_name: 'fr-FR-Chirp-HD-F', ssml_gender: 'FEMALE', voice_type: 'Chirp-HD' },
    { language_code: 'fr-FR', voice_name: 'fr-FR-Chirp-HD-O', ssml_gender: 'FEMALE', voice_type: 'Chirp-HD' },
    { language_code: 'fr-FR', voice_name: 'fr-FR-Studio-A', ssml_gender: 'FEMALE', voice_type: 'Studio' },
    { language_code: 'fr-FR', voice_name: 'fr-FR-Studio-D', ssml_gender: 'MALE', voice_type: 'Studio' },
    { language_code: 'fr-FR', voice_name: 'fr-FR-Neural2-F', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'fr-FR', voice_name: 'fr-FR-Neural2-G', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'fr-FR', voice_name: 'fr-FR-Polyglot-1', ssml_gender: 'MALE', voice_type: 'Polyglot' },
    { language_code: 'fr-FR', voice_name: 'fr-FR-Wavenet-F', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'fr-FR', voice_name: 'fr-FR-Wavenet-G', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'fr-FR', voice_name: 'fr-FR-Standard-F', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'fr-FR', voice_name: 'fr-FR-Standard-G', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── German ────────────────────────────────────────────────
  'de-DE': [
    { language_code: 'de-DE', voice_name: 'de-DE-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'de-DE', voice_name: 'de-DE-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'de-DE', voice_name: 'de-DE-Chirp-HD-D', ssml_gender: 'MALE', voice_type: 'Chirp-HD' },
    { language_code: 'de-DE', voice_name: 'de-DE-Chirp-HD-F', ssml_gender: 'FEMALE', voice_type: 'Chirp-HD' },
    { language_code: 'de-DE', voice_name: 'de-DE-Chirp-HD-O', ssml_gender: 'FEMALE', voice_type: 'Chirp-HD' },
    { language_code: 'de-DE', voice_name: 'de-DE-Studio-B', ssml_gender: 'MALE', voice_type: 'Studio' },
    { language_code: 'de-DE', voice_name: 'de-DE-Studio-C', ssml_gender: 'FEMALE', voice_type: 'Studio' },
    { language_code: 'de-DE', voice_name: 'de-DE-Neural2-G', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'de-DE', voice_name: 'de-DE-Neural2-H', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'de-DE', voice_name: 'de-DE-Polyglot-1', ssml_gender: 'MALE', voice_type: 'Polyglot' },
    { language_code: 'de-DE', voice_name: 'de-DE-Wavenet-G', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'de-DE', voice_name: 'de-DE-Wavenet-H', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'de-DE', voice_name: 'de-DE-Standard-G', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'de-DE', voice_name: 'de-DE-Standard-H', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── Chinese (Simplified) ──────────────────────────────────
  'zh-CN': [
    { language_code: 'zh-CN', voice_name: 'cmn-CN-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'zh-CN', voice_name: 'cmn-CN-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'zh-CN', voice_name: 'cmn-CN-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'zh-CN', voice_name: 'cmn-CN-Wavenet-B', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'zh-CN', voice_name: 'cmn-CN-Wavenet-C', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'zh-CN', voice_name: 'cmn-CN-Wavenet-D', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'zh-CN', voice_name: 'cmn-CN-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'zh-CN', voice_name: 'cmn-CN-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'zh-CN', voice_name: 'cmn-CN-Standard-C', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'zh-CN', voice_name: 'cmn-CN-Standard-D', ssml_gender: 'FEMALE', voice_type: 'Standard' },
  ],

  // ── Japanese ──────────────────────────────────────────────
  'ja-JP': [
    { language_code: 'ja-JP', voice_name: 'ja-JP-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'ja-JP', voice_name: 'ja-JP-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'ja-JP', voice_name: 'ja-JP-Neural2-B', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'ja-JP', voice_name: 'ja-JP-Neural2-C', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'ja-JP', voice_name: 'ja-JP-Neural2-D', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'ja-JP', voice_name: 'ja-JP-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'ja-JP', voice_name: 'ja-JP-Wavenet-B', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'ja-JP', voice_name: 'ja-JP-Wavenet-C', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'ja-JP', voice_name: 'ja-JP-Wavenet-D', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'ja-JP', voice_name: 'ja-JP-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'ja-JP', voice_name: 'ja-JP-Standard-B', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'ja-JP', voice_name: 'ja-JP-Standard-C', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'ja-JP', voice_name: 'ja-JP-Standard-D', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── Korean ────────────────────────────────────────────────
  'ko-KR': [
    { language_code: 'ko-KR', voice_name: 'ko-KR-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'ko-KR', voice_name: 'ko-KR-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'ko-KR', voice_name: 'ko-KR-Neural2-A', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'ko-KR', voice_name: 'ko-KR-Neural2-B', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'ko-KR', voice_name: 'ko-KR-Neural2-C', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'ko-KR', voice_name: 'ko-KR-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'ko-KR', voice_name: 'ko-KR-Wavenet-B', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'ko-KR', voice_name: 'ko-KR-Wavenet-C', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'ko-KR', voice_name: 'ko-KR-Wavenet-D', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'ko-KR', voice_name: 'ko-KR-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'ko-KR', voice_name: 'ko-KR-Standard-B', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'ko-KR', voice_name: 'ko-KR-Standard-C', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'ko-KR', voice_name: 'ko-KR-Standard-D', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── Portuguese (Brazil) ──────────────────────────────────
  'pt-BR': [
    { language_code: 'pt-BR', voice_name: 'pt-BR-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'pt-BR', voice_name: 'pt-BR-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'pt-BR', voice_name: 'pt-BR-Neural2-A', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'pt-BR', voice_name: 'pt-BR-Neural2-B', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'pt-BR', voice_name: 'pt-BR-Neural2-C', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'pt-BR', voice_name: 'pt-BR-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'pt-BR', voice_name: 'pt-BR-Wavenet-B', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'pt-BR', voice_name: 'pt-BR-Wavenet-C', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'pt-BR', voice_name: 'pt-BR-Wavenet-D', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'pt-BR', voice_name: 'pt-BR-Wavenet-E', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'pt-BR', voice_name: 'pt-BR-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'pt-BR', voice_name: 'pt-BR-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'pt-BR', voice_name: 'pt-BR-Standard-C', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'pt-BR', voice_name: 'pt-BR-Standard-D', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'pt-BR', voice_name: 'pt-BR-Standard-E', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── Italian ─────────────────────────────────────────────
  'it-IT': [
    { language_code: 'it-IT', voice_name: 'it-IT-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'it-IT', voice_name: 'it-IT-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'it-IT', voice_name: 'it-IT-Chirp-HD-D', ssml_gender: 'MALE', voice_type: 'Chirp-HD' },
    { language_code: 'it-IT', voice_name: 'it-IT-Chirp-HD-F', ssml_gender: 'FEMALE', voice_type: 'Chirp-HD' },
    { language_code: 'it-IT', voice_name: 'it-IT-Chirp-HD-O', ssml_gender: 'FEMALE', voice_type: 'Chirp-HD' },
    { language_code: 'it-IT', voice_name: 'it-IT-Neural2-A', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'it-IT', voice_name: 'it-IT-Neural2-E', ssml_gender: 'FEMALE', voice_type: 'Neural2' },
    { language_code: 'it-IT', voice_name: 'it-IT-Neural2-F', ssml_gender: 'MALE', voice_type: 'Neural2' },
    { language_code: 'it-IT', voice_name: 'it-IT-Wavenet-E', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'it-IT', voice_name: 'it-IT-Wavenet-F', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'it-IT', voice_name: 'it-IT-Standard-E', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'it-IT', voice_name: 'it-IT-Standard-F', ssml_gender: 'MALE', voice_type: 'Standard' },
  ],

  // ── Arabic ────────────────────────────────────────────────
  'ar-SA': [
    { language_code: 'ar-SA', voice_name: 'ar-XA-Chirp3-HD-Achernar', ssml_gender: 'FEMALE', voice_type: 'Chirp3-HD' },
    { language_code: 'ar-SA', voice_name: 'ar-XA-Chirp3-HD-Puck', ssml_gender: 'MALE', voice_type: 'Chirp3-HD' },
    { language_code: 'ar-SA', voice_name: 'ar-XA-Wavenet-A', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'ar-SA', voice_name: 'ar-XA-Wavenet-B', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'ar-SA', voice_name: 'ar-XA-Wavenet-C', ssml_gender: 'MALE', voice_type: 'WaveNet' },
    { language_code: 'ar-SA', voice_name: 'ar-XA-Wavenet-D', ssml_gender: 'FEMALE', voice_type: 'WaveNet' },
    { language_code: 'ar-SA', voice_name: 'ar-XA-Standard-A', ssml_gender: 'FEMALE', voice_type: 'Standard' },
    { language_code: 'ar-SA', voice_name: 'ar-XA-Standard-B', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'ar-SA', voice_name: 'ar-XA-Standard-C', ssml_gender: 'MALE', voice_type: 'Standard' },
    { language_code: 'ar-SA', voice_name: 'ar-XA-Standard-D', ssml_gender: 'FEMALE', voice_type: 'Standard' },
  ],
};

/**
 * TTS API prefix mapping for languages where the API locale differs from the UI code.
 * e.g. UI uses 'zh-CN' but Google TTS API uses 'cmn-CN' for Mandarin Chinese.
 */
const ttsPrefixMap: Record<string, string> = {
  'zh-CN': 'cmn-CN',
  'ar-SA': 'ar-XA',
};

/**
 * Build the full voice list per language by merging:
 *   - All 30 Chirp3-HD voices (generated)
 *   - Non-Chirp3-HD voices from the curated static list
 */
const voicesByLanguage: Record<string, VoiceInfo[]> = Object.fromEntries(
  Object.entries(staticVoicesByLanguage).map(([lang, voices]) => {
    const non_chirp3 = voices.filter(v => v.voice_type !== 'Chirp3-HD');
    const all_chirp3 = generateChirp3HDVoices(lang, ttsPrefixMap[lang]);
    return [lang, [...all_chirp3, ...non_chirp3]];
  })
);

/**
 * Get individual voices for a language + voice type combination
 */
export function getVoicesForLanguageAndType(language_code: string, voice_type: VoiceType): VoiceInfo[] {
  const voices = voicesByLanguage[language_code] || [];
  return voices.filter(v => v.voice_type === voice_type);
}

/**
 * Get all voices for a language
 */
export function getAllVoicesForLanguage(language_code: string): VoiceInfo[] {
  return voicesByLanguage[language_code] || [];
}

/**
 * Format voice name for display: "Neural2-J (Male)" or "Chirp3-HD Achernar (Female)"
 */
export function formatVoiceLabel(voice: VoiceInfo): string {
  const name_parts = voice.voice_name.split('-');
  // For Chirp voices: show the character name e.g. "Achernar"
  // For others: show the letter e.g. "A", "B"
  const short_name = voice.voice_type.startsWith('Chirp')
    ? name_parts[name_parts.length - 1]
    : `${voice.voice_type}-${name_parts[name_parts.length - 1]}`;
  const gender_label = voice.ssml_gender === 'MALE' ? 'Male' : voice.ssml_gender === 'FEMALE' ? 'Female' : 'Neutral';
  return `${short_name} (${gender_label})`;
}

/**
 * Available voice types per language based on Google Cloud TTS API
 * Reference: https://docs.cloud.google.com/text-to-speech/docs/list-voices-and-types
 * Order: premium/newest first, standard last
 */
const voiceTypesByLanguage: Record<string, VoiceTypeOption[]> = {
  // ── English variants ──────────────────────────────────────
  'en-US': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'Chirp-HD', label: 'Chirp HD', description: 'Natural & expressive' },
    { value: 'Studio', label: 'Studio', description: 'Professional studio quality' },
    { value: 'Neural2', label: 'Neural2', description: 'Premium neural voice' },
    { value: 'News', label: 'News', description: 'News anchor style' },
    { value: 'Casual', label: 'Casual', description: 'Conversational tone' },
    { value: 'Polyglot', label: 'Polyglot', description: 'Multi-language support' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  'en-GB': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'Chirp-HD', label: 'Chirp HD', description: 'Natural & expressive' },
    { value: 'Studio', label: 'Studio', description: 'Professional studio quality' },
    { value: 'Neural2', label: 'Neural2', description: 'Premium neural voice' },
    { value: 'News', label: 'News', description: 'News anchor style' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  'en-AU': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'Chirp-HD', label: 'Chirp HD', description: 'Natural & expressive' },
    { value: 'Neural2', label: 'Neural2', description: 'Premium neural voice' },
    { value: 'News', label: 'News', description: 'News anchor style' },
    { value: 'Polyglot', label: 'Polyglot', description: 'Multi-language support' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  // Only the types en-IN and en-GB share (Studio/News exist in en-GB but not en-IN).
  'en-IN': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'Chirp-HD', label: 'Chirp HD', description: 'Natural & expressive' },
    { value: 'Neural2', label: 'Neural2', description: 'Premium neural voice' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],

  // ── Hindi ─────────────────────────────────────────────────
  'hi-IN': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'Neural2', label: 'Neural2', description: 'Premium neural voice' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],

  // ── Indian regional languages ─────────────────────────────
  'bn-IN': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  'te-IN': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  'mr-IN': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  'ta-IN': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  'gu-IN': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  'kn-IN': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  'ml-IN': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  'pa-IN': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  'ur-IN': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],

  // ── European languages ────────────────────────────────────
  'es-ES': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'Chirp-HD', label: 'Chirp HD', description: 'Natural & expressive' },
    { value: 'Studio', label: 'Studio', description: 'Professional studio quality' },
    { value: 'Neural2', label: 'Neural2', description: 'Premium neural voice' },
    { value: 'Polyglot', label: 'Polyglot', description: 'Multi-language support' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  'fr-FR': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'Chirp-HD', label: 'Chirp HD', description: 'Natural & expressive' },
    { value: 'Studio', label: 'Studio', description: 'Professional studio quality' },
    { value: 'Neural2', label: 'Neural2', description: 'Premium neural voice' },
    { value: 'Polyglot', label: 'Polyglot', description: 'Multi-language support' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  'de-DE': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'Chirp-HD', label: 'Chirp HD', description: 'Natural & expressive' },
    { value: 'Studio', label: 'Studio', description: 'Professional studio quality' },
    { value: 'Neural2', label: 'Neural2', description: 'Premium neural voice' },
    { value: 'Polyglot', label: 'Polyglot', description: 'Multi-language support' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  'it-IT': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'Chirp-HD', label: 'Chirp HD', description: 'Natural & expressive' },
    { value: 'Neural2', label: 'Neural2', description: 'Premium neural voice' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  'pt-BR': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'Neural2', label: 'Neural2', description: 'Premium neural voice' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],

  // ── Asian languages ───────────────────────────────────────
  'zh-CN': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  'ja-JP': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'Neural2', label: 'Neural2', description: 'Premium neural voice' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
  'ko-KR': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'Neural2', label: 'Neural2', description: 'Premium neural voice' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],

  // ── Arabic ────────────────────────────────────────────────
  'ar-SA': [
    { value: 'Chirp3-HD', label: 'Chirp 3 HD', description: 'Latest, most natural' },
    { value: 'WaveNet', label: 'WaveNet', description: 'High quality synthesis' },
    { value: 'Standard', label: 'Standard', description: 'Basic voice' },
  ],
};

/** Default fallback voice types */
const defaultVoiceTypes: VoiceTypeOption[] = [
  { value: 'Standard', label: 'Standard', description: 'Basic voice' },
];

/**
 * Get available voice types for a given language code
 */
export function getVoiceTypesForLanguage(language_code: string): VoiceTypeOption[] {
  return voiceTypesByLanguage[language_code] || defaultVoiceTypes;
}

/**
 * Get the default (best) voice type for a given language code
 */
export function getDefaultVoiceType(language_code: string): VoiceType {
  const types = getVoiceTypesForLanguage(language_code);
  return types[0].value;
}

/**
 * Check if a voice type is available for a given language
 */
export function isVoiceTypeAvailable(language_code: string, voice_type: string): boolean {
  const types = getVoiceTypesForLanguage(language_code);
  return types.some(t => t.value === voice_type);
}

/**
 * Voice types that do NOT support pitch adjustment in Google Cloud TTS.
 * When selected, the pitch slider should be disabled in the UI.
 */
const PITCH_UNSUPPORTED_VOICE_TYPES: Set<VoiceType> = new Set(['Chirp3-HD', 'Chirp-HD', 'Casual']);

/**
 * Check if a voice type supports pitch adjustment
 */
export function isVoiceTypePitchSupported(voice_type: VoiceType): boolean {
  return !PITCH_UNSUPPORTED_VOICE_TYPES.has(voice_type);
}

/**
 * Language options with detailed structure for AI chat components
 * Includes separate code, name, and flag fields
 */
export const languageOptionsDetailed: readonly LanguageOptionDetailed[] = [
  // English Variants
  { code: 'en-US', name: 'English (US)', flag: '🇺🇸' },
  { code: 'en-GB', name: 'English (UK)', flag: '🇬🇧' },
  { code: 'en-AU', name: 'English (Australia)', flag: '🇦🇺' },
  { code: 'en-IN', name: 'English (India)', flag: '🇮🇳' },
  // Indian Languages
  { code: 'hi-IN', name: 'Hindi (हिन्दी)', flag: '🇮🇳' },
  { code: 'bn-IN', name: 'Bengali (বাংলা)', flag: '🇮🇳' },
  { code: 'te-IN', name: 'Telugu (తెలుగు)', flag: '🇮🇳' },
  { code: 'mr-IN', name: 'Marathi (मराठी)', flag: '🇮🇳' },
  { code: 'ta-IN', name: 'Tamil (தமிழ்)', flag: '🇮🇳' },
  { code: 'gu-IN', name: 'Gujarati (ગુજરાતી)', flag: '🇮🇳' },
  { code: 'kn-IN', name: 'Kannada (ಕನ್ನಡ)', flag: '🇮🇳' },
  { code: 'ml-IN', name: 'Malayalam (മലയാളം)', flag: '🇮🇳' },
  { code: 'pa-IN', name: 'Punjabi (ਪੰਜਾਬੀ)', flag: '🇮🇳' },
  { code: 'ur-IN', name: 'Urdu (اردو)', flag: '🇮🇳' },
  // Other Languages
  { code: 'es-ES', name: 'Spanish (Spain)', flag: '🇪🇸' },
  { code: 'fr-FR', name: 'French', flag: '🇫🇷' },
  { code: 'de-DE', name: 'German', flag: '🇩🇪' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)', flag: '🇵🇹' },
  { code: 'it-IT', name: 'Italian', flag: '🇮🇹' },
  { code: 'zh-CN', name: 'Chinese (Simplified)', flag: '🇨🇳' },
  { code: 'ja-JP', name: 'Japanese', flag: '🇯🇵' },
  { code: 'ko-KR', name: 'Korean', flag: '🇰🇷' },
  { code: 'ar-SA', name: 'Arabic', flag: '🇸🇦' }
] as const;

/**
 * Localized preview text for TTS voice preview per language
 */
const previewTextByLanguage: Record<string, string> = {
  'en-US': 'Hello, this is a preview of the selected voice settings for your course.',
  'en-GB': 'Hello, this is a preview of the selected voice settings for your course.',
  'en-AU': 'Hello, this is a preview of the selected voice settings for your course.',
  'en-IN': 'Hello, this is a preview of the selected voice settings for your course.',
  'hi-IN': 'नमस्ते, यह आपके कोर्स के लिए चुनी गई आवाज़ सेटिंग्स का एक पूर्वावलोकन है।',
  'bn-IN': 'নমস্কার, এটি আপনার কোর্সের জন্য নির্বাচিত ভয়েস সেটিংসের একটি পূর্বরূপ।',
  'te-IN': 'నమస్కారం, ఇది మీ కోర్సు కోసం ఎంచుకున్న వాయిస్ సెట్టింగ్‌ల ప్రివ్యూ.',
  'mr-IN': 'नमस्कार, हे तुमच्या कोर्ससाठी निवडलेल्या आवाज सेटिंग्जचे पूर्वावलोकन आहे.',
  'ta-IN': 'வணக்கம், இது உங்கள் பாடத்திற்காக தேர்ந்தெடுக்கப்பட்ட குரல் அமைப்புகளின் முன்னோட்டம்.',
  'gu-IN': 'નમસ્તે, આ તમારા કોર્સ માટે પસંદ કરેલ અવાજ સેટિંગ્સનું પૂર્વાવલોકન છે.',
  'kn-IN': 'ನಮಸ್ಕಾರ, ಇದು ನಿಮ್ಮ ಕೋರ್ಸ್‌ಗಾಗಿ ಆಯ್ಕೆ ಮಾಡಿದ ಧ್ವನಿ ಸೆಟ್ಟಿಂಗ್‌ಗಳ ಪೂರ್ವವೀಕ್ಷಣೆ.',
  'ml-IN': 'നമസ്കാരം, ഇത് നിങ്ങളുടെ കോഴ്‌സിനായി തിരഞ്ഞെടുത്ത വോയ്‌സ് ക്രമീകരണങ്ങളുടെ പ്രിവ്യൂ ആണ്.',
  'pa-IN': 'ਸਤ ਸ੍ਰੀ ਅਕਾਲ, ਇਹ ਤੁਹਾਡੇ ਕੋਰਸ ਲਈ ਚੁਣੀਆਂ ਗਈਆਂ ਅਵਾਜ਼ ਸੈਟਿੰਗਾਂ ਦਾ ਪੂਰਵ ਦਰਸ਼ਨ ਹੈ।',
  'ur-IN': 'السلام علیکم، یہ آپ کے کورس کے لیے منتخب آواز کی ترتیبات کا پیش نظارہ ہے۔',
  'es-ES': 'Hola, esta es una vista previa de la configuración de voz seleccionada para su curso.',
  'fr-FR': 'Bonjour, ceci est un aperçu des paramètres vocaux sélectionnés pour votre cours.',
  'de-DE': 'Hallo, dies ist eine Vorschau der ausgewählten Spracheinstellungen für Ihren Kurs.',
  'zh-CN': '您好，这是您课程所选语音设置的预览。',
  'ja-JP': 'こんにちは、これはコースに選択した音声設定のプレビューです。',
  'ko-KR': '안녕하세요, 이것은 코스에 선택한 음성 설정의 미리보기입니다.',
  'pt-BR': 'Olá, esta é uma prévia das configurações de voz selecionadas para o seu curso.',
  'it-IT': 'Ciao, questa è un\'anteprima delle impostazioni vocali selezionate per il tuo corso.',
  'ar-SA': 'مرحباً، هذه معاينة لإعدادات الصوت المختارة لدورتك التدريبية.',
};

/**
 * Get localized preview text for TTS voice preview
 */
export function getPreviewText(language_code: string): string {
  return previewTextByLanguage[language_code]
    || previewTextByLanguage['en-US'];
}
